import SpineWrapper, { ICancelablePromise } from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import EventsCenter, { IWildcardEventData } from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';

interface IReelTensionAnimationFlags {
    [EGameEvents.TENSION_START_$]: boolean;
    [EGameEvents.TENSION_INTRO_$]: boolean;
    [EGameEvents.TENSION_WIN_$]: boolean;
    [EGameEvents.TENSION_LOSE_$]: boolean;
}

export class Tension extends GameComponent {
    static readonly SPINE_KEY: string = 'tension';
    static readonly SKIN_NAME: string = 'default';
    public scene: GameScene;
    public spine: SpineWrapper;
    private interrupted: boolean;
    private spineAnimationFlags: Map<number, IReelTensionAnimationFlags>;
    private tensionEndPromise: ICancelablePromise | undefined;
    constructor(scene: GameScene) {
        super(scene);
        this.scene = scene;
        Logger.log(LoggerLevel.Constructor, {
            source: LoggerSource.Game,
            message: 'Tension constructor'
        });
    }

    public onInit(): void {
        EventsCenter.on(EGameEvents.TENSION_START_$, (data: IWildcardEventData) => this.switchToLoop(true, data.eventIds[0]), this);
        EventsCenter.on(EGameEvents.TENSION_INTRO_$, (data: IWildcardEventData) => this.switchToLoop(false, data.eventIds[0]), this);
        EventsCenter.on(EGameEvents.TENSION_LOSE_$, (data: IWildcardEventData) => this.onTensionEnd(false, data.eventIds[0]), this);
        EventsCenter.on(EGameEvents.TENSION_WIN_$, (data: IWildcardEventData) => this.onTensionEnd(true, data.eventIds[0]), this);
        EventsCenter.on(EGameEvents.INTERRUPT_TENSION, this.interruptTension, this);
        EventsCenter.on(EGameStates.PRE_SPIN_STATE, this.interruptTension, this);
        EventsCenter.on(EGameStates.SPIN_STATE, this.reset, this);
    }

    private async switchToLoop(isStart: boolean, index: number): Promise<void> {
        if (this.interrupted) return;
        const currentReelAnimations = this.spineAnimationFlags.get(index);
        const event = isStart ? EGameEvents.TENSION_START_$ : EGameEvents.TENSION_INTRO_$;
        const hasAnimation = !!currentReelAnimations?.[event];
        if (isStart) this.spine.spine.visible = true;
        if (hasAnimation) await this.spine.waitForAnimationEvent(event, 'complete', [index]).promise;
        EventsCenter.emit(EGameEvents.TENSION_LOOP_$, { eventIds: [index] });
    }

    private async onTensionEnd(isWinning: boolean, index: number) {
        const currentReelAnimations = this.spineAnimationFlags.get(index);
        const event = isWinning ? EGameEvents.TENSION_WIN_$ : EGameEvents.TENSION_LOSE_$;
        const hasAnimation = !!currentReelAnimations?.[event];
        if (hasAnimation) {
            this.tensionEndPromise = this.spine.waitForAnimationEvent(event, 'complete', [index]);
            await this.tensionEndPromise.promise;
        }
        this.spine.spine.visible = false;
    }

    public onCreate(): void {
        this.spine = new SpineWrapper(
            this.scene,
            {
                x: this.scene.screen.width / 2,
                y: this.scene.screen.height / 2
            },
            { dataKey: Tension.SPINE_KEY, skinName: Tension.SKIN_NAME },
            true
        )!;
        const numberOfReels = this.scene.game.gameStore.get(EGameStoreKeys.NumberOfReels);
        this.spineAnimationFlags = new Map();
        for (let index = 1; index <= numberOfReels; index++) {
            this.spineAnimationFlags.set(index, {
                [EGameEvents.TENSION_START_$]: this.spine.eventManager.hasGameEventAnimation(EGameEvents.TENSION_START_$, undefined, [index]),
                [EGameEvents.TENSION_INTRO_$]: this.spine.eventManager.hasGameEventAnimation(EGameEvents.TENSION_INTRO_$, undefined, [index]),
                [EGameEvents.TENSION_WIN_$]: this.spine.eventManager.hasGameEventAnimation(EGameEvents.TENSION_WIN_$, undefined, [index]),
                [EGameEvents.TENSION_LOSE_$]: this.spine.eventManager.hasGameEventAnimation(EGameEvents.TENSION_LOSE_$, undefined, [index])
            });
        }
        this.spine.spine.zIndex = ELayeringOrder.TENSION;
        this.spine.spine.visible = false;
    }

    private interruptTension(): void {
        this.tensionEndPromise?.cancel();
        this.spine.spine.visible = false;
        this.interrupted = true;
    }

    private reset(): void {
        this.interrupted = false;
    }
}
